/* eslint-disable no-console */
import * as Sentry from '@sentry/react';
import clevertap from 'clevertap-web-sdk';
import { v4 as uuidv4 } from 'uuid';
import packageJson from '../../package.json';
import { VERSION_HEADER_KEY } from '../constants/config';
import { Product } from '../enums/booking';
import { getUniformProduct } from './booking';
import { CookieValue, getCookieHelpers } from './cookie';
import { getUserInfo } from './user';

type AnalyticsParams = {
    bookingParams?: any;
    additionalParams?: Record<string, boolean | number | string | null>;
};

type RecordEvent = (name: string, data: AnalyticsParams) => void;

/**
 * Initialize CleverTap
 */
export const initClevertap = () => {
    try {
        clevertap.init('R5Z-849-WZ4Z', 'in1');
    } catch (error) {
        console.log('Failed to initialize clevertap', error);
    }
};

/**
 * Initialize Sentry
 */
export const initSentry = () => {
    try {
        Sentry.init({
            dsn: 'https://5a6bc29339bdfd1dcbbb6524227426b3@o494858.ingest.sentry.io/4506426197278720',
            ignoreErrors: [
                'Network Error',
                'Request failed with status code 401',
                'Request failed with status code 404',
                'Request failed with status code 503',
                'Java exception was raised during method invocation',
                'Java object is gone',
                'Load failed',
                'Importing a module script failed.',
                'PhonePe is not defined',
                "'text/html' is not a valid JavaScript MIME type.",
                /Failed to fetch/,
            ],
            integrations: [new Sentry.Replay()],
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
            tracesSampleRate: 0.03,
        });
    } catch (error) {
        console.log('Failed to initialize sentry', error);
    }
};

const processTransactionsData = (data: any) => {
    const processedData: Record<string, string> = {};

    const bankCodeArr: string[] = [];
    const paymentModeArr: string[] = [];
    const pgProviderArr: string[] = [];
    const pgwArr: string[] = [];

    data.payment?.forEach((item: any) => {
        if (item.bankCode) bankCodeArr.push(item.bankCode);
        if (item.mode) paymentModeArr.push(item.mode);
        if (item.pgProvider) pgProviderArr.push(item.pgProvider);
        if (item.pgw) pgwArr.push(item.pgw);
    });

    processedData.pgProvider = pgProviderArr.length ? pgProviderArr.join('*') : 'NA';
    processedData.paymentMode = paymentModeArr.length ? paymentModeArr.join('*') : 'NA';
    processedData.bankCode = bankCodeArr.length ? bankCodeArr.join('*') : 'NA';
    processedData.pgw = pgwArr.length ? pgwArr.join('*') : 'NA';

    if (data.refund?.length) {
        const refundPgProviderArr: string[] = [];
        const refundModeArr: string[] = [];

        data.refund.forEach((item: any) => {
            if (item.mode) refundModeArr.push(item.mode);
            if (item.pgProvider) refundPgProviderArr.push(item.pgProvider);
        });

        processedData.refundPgProvider = refundPgProviderArr.length ? refundPgProviderArr.join('*') : 'NA';
        processedData.refundMode = refundModeArr.length ? refundModeArr.join('*') : 'NA';
    }

    return processedData;
};

const processBookingByProduct = (productData: any, product: Product) => {
    const processedData: Record<string, string | number | boolean> = processTransactionsData(productData);

    switch (product) {
        case Product.BUS: {
            processedData.guest = productData.isLoginTicket;
            processedData.origin = productData.origin;
            processedData.destination = productData.destination;
            processedData.journeyDate = productData.journeyDate;
            processedData.isAbhiAssured = productData.isAbhiAssured;
            processedData.isFreeCancellation = productData.isFreeCancellation;
            processedData.ticketType = productData.ticketType;

            break;
        }

        case Product.FLIGHT: {
            processedData.destination = productData.destination;
            processedData.fareType = productData.fareTypeDetails?.fareType || 'NA';
            processedData.isInternational = productData.isInternational;
            processedData.isRoundTrip = productData.isRoundTrip;
            processedData.ixigoAssured = productData.ixigoAssured;
            processedData.ixigoFlex = productData.ixigoFlex;
            processedData.onwardAirline = productData.airline?.length ? productData.airline.join('*') : 'NA';
            processedData.onwardArrivalTime = productData.arrivalTime;
            processedData.onwardDepartureTime = productData.departureTime;
            processedData.origin = productData.origin;
            processedData.pnr = productData.bookingId;
            processedData.travelDateDuration = productData.travelDateDuration > 0 ? productData.travelDateDuration : 0;

            if (productData.isRoundTrip) {
                processedData.returnAirline = productData.roundTrip?.airline.length
                    ? productData.roundTrip?.airline.join('*')
                    : 'NA';
                processedData.returnArrivalTime = productData.roundTrip?.arrivalTime || 'NA';
                processedData.returnDepartureTime = productData.roundTrip?.departureTime || 'NA';
            }

            break;
        }

        case Product.HOTEL: {
            processedData.hotelName = productData.hotelInfo?.name;
            processedData.roomType = productData.roomInfo?.type;
            processedData.roomCount = productData.roomInfo?.count;
            processedData.guestCount = productData.guestInfo?.totalGuests;

            break;
        }

        case Product.TRAIN: {
            processedData.arrivalTime = productData.arrivalTime;
            processedData.departureTime = productData.departureTime;
            processedData.destination = productData.destination;
            processedData.ixiBook = productData.ixiBook;
            processedData.ixigoAssured = productData.ixigoAssured;
            processedData.ixigoFlex = productData.ixigoFlex;
            processedData.origin = productData.origin;
            processedData.pnr = productData.pnr;
            processedData.travelDateDuration = productData.travelDateDuration > 0 ? productData.travelDateDuration : 0;

            break;
        }

        default:
            break;
    }

    return processedData;
};

export const getAnalyticsData = ({ bookingParams, additionalParams = {} }: AnalyticsParams) =>
    getUserInfo().then((userInfo) => {
        const { userId = 'guest' } = userInfo || {};
        const { clientId: appId, appVersion } = getCookieHelpers().getAllCookies();
        const { product, status } = bookingParams || {};
        const uniformProductName = getUniformProduct(product || '') as Product;

        let { bookingId: tripId } = bookingParams || {};
        if (uniformProductName === Product.BUS && !tripId) tripId = bookingParams.orderID;

        const commonData: Record<string, CookieValue> = {
            appId,
            appVersion,
            userId,
            tripId: tripId || 'NA',
            product: uniformProductName?.toUpperCase(),
            tripStatus: status || 'NA',
            uiVersion: 2,
        };

        let bookingData: Record<string, string | number | boolean> = {};
        if (bookingParams) bookingData = processBookingByProduct(bookingParams, uniformProductName);

        let providerId = '';
        if ([Product.FLIGHT, Product.HOTEL].includes(uniformProductName))
            providerId = bookingParams.provider?.id as string;
        if (providerId) commonData.providerId = providerId;

        Object.keys(additionalParams).forEach((key) => {
            commonData[key] = additionalParams[key];
        });

        return { commonData, bookingData };
    });

const callAPIs = (name: string, data: Record<string, string | number | boolean | null>) => {
    const clevertapData: Record<string, string> = {};
    const dwhMetadata: Record<string, string>[] = [];
    const dwhDataArr = [];

    Object.keys(data).forEach((key) => {
        const value = (data[key] ?? '') as string;

        clevertapData[key] = value === '' ? 'NA' : value;
        dwhMetadata.push({ name: key, value: String(value) });
    });

    clevertap.event.push(name, clevertapData);

    dwhDataArr.push({
        id: uuidv4(),
        lob: 'CXP',
        type: 'event',
        timestamp: Date.now(),
        project: name.split(' ')[0],
        metadata: dwhMetadata,
        user_id: data.userId,
        name,
    });

    fetch(`${window.location.origin}/support/tracker/events`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            [VERSION_HEADER_KEY]: packageJson.version,
        },
        body: JSON.stringify(dwhDataArr),
    }).catch((err) => {
        console.log('An error occurred while posting event to DWH: ', err?.message);
    });
};

export const recordEvent: RecordEvent = (name, data) => {
    if (!name) return;

    getAnalyticsData(data)
        .then(({ commonData, bookingData }) => {
            const temp = { ...bookingData, ...commonData };
            callAPIs(name, temp);
        })
        .catch();
};

export const recordTaraEvent: (name: string, data: AnalyticsParams['additionalParams']) => void = (name, data = {}) => {
    if (!name) return;

    getUserInfo().then((userInfo) => {
        const { userId = 'guest' } = userInfo || {};
        const { deviceid, clientId, apiKey, ixiSrc, uuid, appVersion } = getCookieHelpers().getAllCookies();

        const temp: Record<string, CookieValue> = {
            apiKey,
            appVersion,
            clientId,
            deviceid,
            ixiSrc,
            userId,
            uuid,
            uiVersion: packageJson.version,
        };

        Object.keys(data).forEach((key) => {
            const value = String(data[key]);
            if (value === '[object Object]') return;
            temp[key] = value;
        });

        callAPIs(name, temp);
    });
};
